import {
  DecommissionASiteScreenType,
  DecommissionASiteSummaryType,
  useDecommissionASiteInputsContext,
  useDecommissionASiteScreenContext,
} from '../../../context/DecommissionASiteContext';
import React, { useContext } from 'react';
import { ButtonContainer } from '../../common/buttonContainer/ButtonContainer';
import { Button, ButtonTheme } from '../../common/button/Button';
import { ChatReasonContext, useHomePageContext } from '../../../context/HomePageContext';
import { startChatSession } from '../../chat/startChatSession';
import { buildChatData } from './util';
import { S1_FULL_SITE_DECOMMISSION } from '../../chat/Chat';
import './DecommissionASiteSummary.css';

export default function DecommissionASiteSummary({ inventoryData, decommissionHandler }: DecommissionASiteSummaryType) {
  const { decommissionASiteInputs, setDecommissionASiteInputs } = useDecommissionASiteInputsContext();
  const { homePageState, setHomePageState } = useHomePageContext();
  const { setScreen } = useDecommissionASiteScreenContext();
  const { chatReason } = useContext(ChatReasonContext);

  const isDevicesFound = inventoryData.data.length > 0;
  const isChfDevicePresent = inventoryData.data.some((device) => device.deviceType === 'CHF');
  // filters out IHD and PPMID as they can be decommissioned regardless if they are smets 1 or smets 2
  const filteredDevicesList = inventoryData.data.filter((device) =>
    device.deviceType !== 'IHD' ? device.deviceType !== 'PPMID' : false,
  );
  const isAllDeviceSmets1 = filteredDevicesList.every((device) => device.smetsDeviceType === 'Smets 1');

  const canDecommission = isDevicesFound && isChfDevicePresent && isAllDeviceSmets1;

  function goBackHandler() {
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setDecommissionASiteInputs({
      ...decommissionASiteInputs,
      onsiteRadioAnswer: '',
    });
    setScreen(DecommissionASiteScreenType.INITIAL);
  }

  async function startChatHandler() {
    const chatData = buildChatData(inventoryData, decommissionASiteInputs.mpxn, S1_FULL_SITE_DECOMMISSION);
    await startChatSession({ message: chatData, mpxn: decommissionASiteInputs.mpxn, chatReason });
  }

  function buildDevicesList() {
    return (
      <>
        <div className="m-b-10">
          <p className="informationSubHeading">Address</p>
          <div className="informationSectionText">{inventoryData.address}</div>
        </div>
        <div className="m-b-10">
          <p className="informationSubHeading">Devices list</p>
        </div>
        {inventoryData.data.map(({ deviceId, deviceType, deviceStatus, smetsDeviceType }) => (
          <div key={deviceId} className="informationSection" data-testid={'inventory-check-device-' + deviceType}>
            <div className="informationSectionText">{deviceType}</div>
            <div className="informationRow">
              {deviceStatus && (
                <div>
                  <span className="informationSectionText">{`Status: ${deviceStatus}`}</span>
                </div>
              )}
              <div>
                <span className="informationSectionText">{`GUID: ${deviceId}`}</span>
              </div>
              <div>
                <span className="informationSectionText">{`Asset Type: ${smetsDeviceType}`}</span>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }

  function buildErrorInformation() {
    let issueError: string = '';
    let issueAction: string = '';
    if (!isAllDeviceSmets1) {
      issueError = 'NOT SUITABLE FOR S1 SITE DECOMMISSION.';
      issueAction = 'PLEASE SEE BELOW AND GO BACK TO ENTER A DIFFERENT MPxN IF THIS IS NOT THE CORRECT SITE.';
    }
    if (!isChfDevicePresent) {
      issueError = 'NO COMMS HUB WAS FOUND.';
      issueAction = 'PLEASE SEE BELOW AND GO BACK TO ENTER A DIFFERENT MPxN IF THIS IS NOT THE CORRECT SITE.';
    }
    if (!isDevicesFound) {
      issueError = 'NO DEVICES WERE FOUND.';
      issueAction = 'PLEASE GO BACK TO ENTER A DIFFERENT MPxN.';
    }

    return (
      <>
        <div className="m-b-10">
          <p className="informationSubHeading">{issueError}</p>
        </div>
        <div className="m-b-10">
          <p className="informationSubHeading">{issueAction}</p>
        </div>
      </>
    );
  }

  function buildErrorScreen() {
    if (!isDevicesFound) {
      return buildErrorInformation();
    }
    if (!isChfDevicePresent) {
      return (
        <>
          {' '}
          {buildErrorInformation()} {buildDevicesList()}{' '}
        </>
      );
    }
    if (!isAllDeviceSmets1) {
      return (
        <>
          {' '}
          {buildErrorInformation()} {buildDevicesList()}{' '}
        </>
      );
    }
  }

  return (
    <div
      id="decommissionASiteSummaryContainer"
      className={`informationPanel ${canDecommission ? 'decommissionASite3ButtonsContainer' : 'decommissionASite2ButtonsContainer'}`}
      data-testid="decommission-a-site-summary-container"
    >
      {canDecommission ? buildDevicesList() : buildErrorScreen()}

      <ButtonContainer>
        {canDecommission && (
          <Button
            buttonId={'decommission-site'}
            buttonText={'Decommission the s1 site'}
            buttonTheme={ButtonTheme.DANGER}
            onClickHandler={decommissionHandler}
          />
        )}
        <Button
          buttonId={'start-chat-decommission-a-site'}
          buttonText={'Start Chat'}
          buttonTheme={ButtonTheme.SECONDARY}
          onClickHandler={startChatHandler}
        />
        <Button
          buttonId={'go-back-decommission-a-site'}
          buttonText={'Go Back'}
          buttonTheme={canDecommission ? ButtonTheme.SECONDARY : ButtonTheme.PRIMARY}
          onClickHandler={goBackHandler}
        />
      </ButtonContainer>
    </div>
  );
}
